'use client'

import { LuneTheme } from '@lune-fe/lune-ui-lib'
import AttachMoney from '@mui/icons-material/AttachMoney'
import CloudDownloadOutlined from '@mui/icons-material/CloudDownloadOutlined'
import ForestOutlined from '@mui/icons-material/ForestOutlined'
import GradientOutlined from '@mui/icons-material/GradientOutlined'
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined'
import PercentOutlined from '@mui/icons-material/PercentOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import SafetyDivider from '@mui/icons-material/SafetyDivider'
import SavingsOutlined from '@mui/icons-material/SavingsOutlined'
import Storefront from '@mui/icons-material/Storefront'
import Box from '@mui/material/Box'
import { FC, useEffect, useState } from 'react'

import SelectableChips from '@/components/SelectableChips'

export type WhoPays = `merchant` | `consumer` | `split`
export type ContributionSize = `percentage` | `delivery` | `purchase` | `roundup`
export type Project = `reforestinguk` | `dac` | `biochar`

const CheckoutChips: FC<{
    setSelectedChips: (selected: (WhoPays | ContributionSize | Project)[]) => void
}> = ({ setSelectedChips }) => {
    const { palette } = LuneTheme

    const [whoPays, setWhoPays] = useState<WhoPays[]>(['merchant'])
    const [contributionSize, setContributionSize] = useState<ContributionSize[]>(['percentage'])
    const [project, setProject] = useState<Project[]>(['reforestinguk'])

    useEffect(() => {
        setSelectedChips([...whoPays, ...contributionSize, ...project])
    }, [whoPays, contributionSize, project, setSelectedChips])

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                gap: 3,
                minWidth: {
                    xs: '380px',
                    sm: '380px',
                    md: 'auto',
                    lg: '380px',
                    xl: '380px',
                },
                flex: '1 1 auto',
            }}
        >
            <SelectableChips<WhoPays>
                multiSelect={false}
                title={
                    <>
                        Who covers the <br /> carbon credit cost?
                    </>
                }
                chips={[
                    {
                        label: 'Merchant pays',
                        value: 'merchant',
                        icon: <Storefront />,
                    },
                    {
                        label: 'Consumer pays',
                        value: 'consumer',
                        icon: <PersonOutlined />,
                    },
                    {
                        label: 'Split',
                        value: 'split',
                        icon: <SafetyDivider />,
                    },
                ]}
                chipsColors={{
                    background: `${palette.Green50}`,
                    text: `${palette.Green500}`,
                }}
                selected={whoPays}
                onSelect={(val) => {
                    setWhoPays(val)
                    // ContributionSize: roundup is not available for merchant or split. Deselect if selected
                    if (
                        (val.includes('merchant') || val.includes(`split`)) &&
                        contributionSize.includes('roundup')
                    ) {
                        setContributionSize(['percentage'])
                    }
                }}
            />
            <SelectableChips<ContributionSize>
                title={<>Contribution</>}
                chips={[
                    {
                        label: 'Percentage contribution',
                        value: 'percentage',
                        icon: <PercentOutlined />,
                    },
                    {
                        label: 'Offset delivery',
                        value: 'delivery',
                        icon: <LocalShippingOutlined />,
                    },
                    {
                        label: 'Offset purchase',
                        value: 'purchase',
                        icon: <AttachMoney />,
                    },
                    {
                        label: 'Round-up',
                        value: 'roundup',
                        icon: <SavingsOutlined />,
                        disable: whoPays.includes('merchant') || whoPays.includes('split'),
                    },
                ]}
                chipsColors={{
                    background: `${palette.Green50}`,
                    text: `${palette.Green500}`,
                }}
                selected={contributionSize}
                onSelect={setContributionSize}
            />
            <SelectableChips<Project>
                title={<>Type of climate project</>}
                chips={[
                    {
                        label: 'Reforesting the UK',
                        value: 'reforestinguk',
                        icon: <ForestOutlined />,
                    },
                    {
                        label: 'Direct Air Capture',
                        value: 'dac',
                        icon: <CloudDownloadOutlined />,
                    },
                    {
                        label: 'Biochar',
                        value: 'biochar',
                        icon: <GradientOutlined />,
                    },
                ]}
                chipsColors={{
                    background: `${palette.Green50}`,
                    text: `${palette.Green500}`,
                }}
                selected={project}
                onSelect={setProject}
            />
        </Box>
    )
}

export default CheckoutChips
